import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";

import { Modal, Button, Form } from "react-bootstrap";
import { createErpSocket } from "../utils/socketIo";
import Select from "react-select";

import toast from "react-hot-toast";
import { getUserData, node_apiUrl } from "./const";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getErpToken, listAdminUsers } from "../erpClient";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import {
  LockIconGrey,
  LockIconSizeOnButton,
  LockIconSizeTable,
  LockIconWhiteOnButton,
} from "../utils/LockIconforPermission";

const CAPITAL_INVESTMENT_LOCK_ICON = "solar:lock-bold";
const CAPITAL_INVESTMENT_NO_VIEW_MSG =
  "No permission to view capital investment ledger.";
const MODAL_SEARCH_MIN_CHARS = 3;

function modalSearchNoOptionsMessage(loading, inputLength) {
  if (loading) return "Loading...";
  if (inputLength < MODAL_SEARCH_MIN_CHARS) {
    return `Type ${MODAL_SEARCH_MIN_CHARS}+ characters to search`;
  }
  return "No options found";
}

function isCapitalInvestmentViewDeniedMessage(message) {
  const s = String(message || "").toLowerCase();
  return (
    s.includes(String(PERMISSIONS.CAPITAL_INVESTMENT_TABLE).toLowerCase()) ||
    (s.includes("missing permission") &&
      (s.includes("capital_investment") || s.includes("capital investment")))
  );
}

function isCapitalInvestmentEditDeniedMessage(message) {
  const s = String(message || "").toLowerCase();
  return s.includes(String(PERMISSIONS.EDIT_CAPITAL_INVESTMENT).toLowerCase());
}

function formatDateToDDMMYYYY(dateStr) {
  if (!dateStr) return "-";
  try {
    const raw = String(dateStr).includes("T")
      ? String(dateStr).split("T")[0]
      : String(dateStr).split(" ")[0];
    const parts = raw.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
}

const CAPITAL_LEDGER_FETCH_DEDUPE_MS = 2000;

let capitalLedgerListPromise = null;
let capitalLedgerListCache = null;
let capitalLedgerListCacheKey = null;
let capitalLedgerListCacheAt = 0;

async function requestCapitalLedgerList(branchId, { force = false } = {}) {
  const key = String(branchId);
  const now = Date.now();

  if (!force) {
    if (capitalLedgerListPromise) return capitalLedgerListPromise;
    if (
      capitalLedgerListCache &&
      capitalLedgerListCacheKey === key &&
      now - capitalLedgerListCacheAt < CAPITAL_LEDGER_FETCH_DEDUPE_MS
    ) {
      return capitalLedgerListCache;
    }
  } else {
    capitalLedgerListPromise = null;
    capitalLedgerListCache = null;
    capitalLedgerListCacheKey = null;
    capitalLedgerListCacheAt = 0;
  }

  capitalLedgerListPromise = (async () => {
    const token = getErpToken();
    const response = await fetch(`${node_apiUrl}/capital_investment/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        type: "ledger_list",
        branch_id: branchId,
        pagination: {
          page: 1,
          limit: 100,
        },
      }),
    });
    const data = await response.json();
    if (data?.success) {
      capitalLedgerListCache = data;
      capitalLedgerListCacheKey = key;
      capitalLedgerListCacheAt = Date.now();
    }
    return data;
  })().finally(() => {
    capitalLedgerListPromise = null;
  });

  return capitalLedgerListPromise;
}

function CapitalInvestmentNoViewTableMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={CAPITAL_INVESTMENT_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{CAPITAL_INVESTMENT_NO_VIEW_MSG}</span>
    </span>
  );
}

function CapitalInvestmentEditLockedHint() {
  return (
    <span
      className="d-inline-flex align-items-center"
      title="No permission to edit capital investment (EDIT_CAPITAL_INVESTMENT)."
      role="img"
      aria-label="No permission to edit capital investment"
    >
      <Icon
        icon={CAPITAL_INVESTMENT_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
    </span>
  );
}

function CapitalInvestmentTable() {
  // const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const [stakeList, setStakeList] = useState([]);
  const [ledgerList, setLedgerList] = useState([]);
  const [investorLookup, setInvestorLookup] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStakeModal, setShowStakeModal] = useState(false);

  // Add Investment form states
  const [investorId, setInvestorId] = useState("");
  const [direction, setDirection] = useState("IN");
  const [amount, setAmount] = useState("");
  const [transactionDate, setTransactionDate] = useState("");
  const [note, setNote] = useState("");
  const [investmentInvestorOptions, setInvestmentInvestorOptions] = useState([]);
  const [investmentOptionsLoading, setInvestmentOptionsLoading] = useState(false);
  const investmentSearchInputRef = useRef("");
  const lastInvestmentSearchApiRef = useRef("");

  // Add Stake form states
  const [stakes, setStakes] = useState([
    { user_admin_id: "", ownership_percent: "" },
  ]);
  const [stakeInvestorOptions, setStakeInvestorOptions] = useState([]);
  const [stakeOptionsLoading, setStakeOptionsLoading] = useState(false);
  const stakeSearchInputRef = useRef("");
  const lastStakeSearchApiRef = useRef("");

  const [socket, setSocket] = useState(null);
  const [editingLedger, setEditingLedger] = useState(null);

  const userData = getUserData();
  const [canViewCapital, setCanViewCapital] = useState(() =>
    hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_TABLE),
  );
  const [canAddCapital, setCanAddCapital] = useState(() =>
    hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_ADD),
  );
  const [canEditCapital, setCanEditCapital] = useState(() =>
    hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_EDIT),
  );

  useEffect(() => {
    const syncPermissions = () => {
      setCanViewCapital(hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_TABLE));
      setCanAddCapital(hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_ADD));
      setCanEditCapital(hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_EDIT));
    };

    window.addEventListener("permissionsUpdated", syncPermissions);
    window.addEventListener("erpAuthUpdated", syncPermissions);
    window.addEventListener("storage", syncPermissions);
    const interval = setInterval(syncPermissions, 1000);

    return () => {
      window.removeEventListener("permissionsUpdated", syncPermissions);
      window.removeEventListener("erpAuthUpdated", syncPermissions);
      window.removeEventListener("storage", syncPermissions);
      clearInterval(interval);
    };
  }, []);

  const resetStakeSearchState = () => {
    setStakeInvestorOptions([]);
    stakeSearchInputRef.current = "";
    lastStakeSearchApiRef.current = "";
  };

  const resetInvestmentSearchState = () => {
    setInvestmentInvestorOptions([]);
    investmentSearchInputRef.current = "";
    lastInvestmentSearchApiRef.current = "";
  };

  // Search employees for stake modal (triggered only when 3+ chars typed)
  const searchEmployees = async (search) => {
    const trimmed = String(search || "").trim();
    if (trimmed.length < MODAL_SEARCH_MIN_CHARS) {
      lastStakeSearchApiRef.current = "";
      setStakeInvestorOptions([]);
      return;
    }
    if (trimmed === lastStakeSearchApiRef.current) return;

    setStakeOptionsLoading(true);
    try {
      const response = await listAdminUsers({ search: trimmed });
      let users = response?.data?.users;
      if (!Array.isArray(users) && Array.isArray(response?.data)) {
        users = response.data;
      }
      if (response?.success && Array.isArray(users)) {
        const lower = trimmed.toLowerCase();
        const filtered = users.filter((u) => {
          const name = String(u.name || "").toLowerCase();
          const phone = String(u.phone || "");
          const email = String(u.email || "").toLowerCase();
          return (
            name.includes(lower) ||
            phone.includes(lower) ||
            email.includes(lower)
          );
        });
        const listToUse = filtered.length > 0 ? filtered : users;
        const options = listToUse.map((user) => ({
          value: user.id,
          label: user.name,
        }));
        lastStakeSearchApiRef.current = trimmed;
        setStakeInvestorOptions(options);

        setInvestorLookup((prev) => {
          const next = { ...prev };
          listToUse.forEach((u) => {
            next[u.id] = u.name;
          });
          return next;
        });
      } else {
        lastStakeSearchApiRef.current = trimmed;
        setStakeInvestorOptions([]);
      }
    } catch (error) {
      console.error("Error searching employees:", error);
      toast.error(error.message || "Failed to search employees");
      setStakeInvestorOptions([]);
    } finally {
      setStakeOptionsLoading(false);
    }
  };

  // Search stakeholders for investment modal (triggered only when 3+ chars typed)
  const searchStakeholders = async (search) => {
    const trimmed = String(search || "").trim();
    if (trimmed.length < MODAL_SEARCH_MIN_CHARS) {
      lastInvestmentSearchApiRef.current = "";
      setInvestmentInvestorOptions([]);
      return;
    }
    if (trimmed === lastInvestmentSearchApiRef.current) return;

    setInvestmentOptionsLoading(true);
    try {
      const token = getErpToken();
      const response = await fetch(`${node_apiUrl}/capital_investment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: "stake_list",
          branch_id: getSessionBranchId(),
          search: trimmed,
        }),
      });

      const data = await response.json();

      if (data?.success && Array.isArray(data?.data?.stakes)) {
        const lower = trimmed.toLowerCase();
        const filtered = data.data.stakes.filter((stake) =>
          String(stake.user_name || "").toLowerCase().includes(lower),
        );
        const listToUse = filtered.length > 0 ? filtered : data.data.stakes;
        const options = listToUse.map((stake) => ({
          value: stake.user_admin_id,
          label: stake.user_name,
        }));
        lastInvestmentSearchApiRef.current = trimmed;
        setInvestmentInvestorOptions(options);

        setInvestorLookup((prev) => {
          const next = { ...prev };
          listToUse.forEach((stake) => {
            next[stake.user_admin_id] = stake.user_name;
          });
          return next;
        });
      } else {
        lastInvestmentSearchApiRef.current = trimmed;
        setInvestmentInvestorOptions([]);
      }
    } catch (error) {
      console.error("Error searching stakeholders:", error);
      toast.error(error.message || "Failed to search stakeholders");
      setInvestmentInvestorOptions([]);
    } finally {
      setInvestmentOptionsLoading(false);
    }
  };

  const fetchLedgerList = useCallback(async ({ force = false } = {}) => {
    if (!canViewCapital) {
      setLedgerList([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await requestCapitalLedgerList(getSessionBranchId(), {
        force,
      });

      if (data?.success && Array.isArray(data?.data?.data)) {
        const fetchedItems = data.data.data.map((ledger) => ({
          id: ledger.id,
          user_admin_id: ledger.user_admin_id,
          direction: ledger.direction,
          amount: ledger.amount,
          note: ledger.note || "",
          transaction_date: ledger.transaction_date,
          investor_name: ledger.investor_name || "",
          created_by_name: ledger.created_by_name || "",
          created_at: ledger.created_at,
        }));

        fetchedItems.sort((a, b) => b.id - a.id);
        setLedgerList(fetchedItems);
      } else {
        setLedgerList([]);
        if (
          data?.success === false &&
          data?.message &&
          !isCapitalInvestmentViewDeniedMessage(data.message) &&
          !isCapitalInvestmentEditDeniedMessage(data.message)
        ) {
          toast.error(data.message);
        }
      }
    } catch (error) {
      console.error("Error fetching ledger list:", error);
      const msg = error?.message || "Error fetching ledger list";
      if (
        !isCapitalInvestmentViewDeniedMessage(msg) &&
        !isCapitalInvestmentEditDeniedMessage(msg)
      ) {
        toast.error(msg);
      }
      setLedgerList([]);
    } finally {
      setIsLoading(false);
    }
  }, [canViewCapital]);

  useEffect(() => {
    void fetchLedgerList();
  }, [fetchLedgerList]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    newSocket.on("connect", () => {
      // console.log("CapitalInvestmentTable: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("CapitalInvestmentTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("CapitalInvestmentTable: Socket connection error:", error);
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Filtering based on search and status
  const filteredLedger = ledgerList.filter((ledger) => {
    const investorName = ledger.investor_name || "";
    const note = ledger.note || "";

    const matchesSearch =
      investorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "IN" && ledger.direction === "IN") ||
      (statusFilter === "OUT" && ledger.direction === "OUT");

    return matchesSearch && matchesStatus;
  });

  // Handle Edit Click
  const handleEditClick = (ledger) => {
    if (!canEditCapital) return;
    setEditingLedger(ledger);
    const uId = String(ledger.user_admin_id);
    const uName = ledger.investor_name || uId;
    setInvestorId(uId);
    setInvestorLookup((prev) => ({ ...prev, [uId]: uName }));
    setInvestmentInvestorOptions([
      { value: ledger.user_admin_id, label: uName },
    ]);
    investmentSearchInputRef.current = "";
    lastInvestmentSearchApiRef.current = "";
    setDirection(ledger.direction);
    setAmount(String(ledger.amount));
    setTransactionDate(ledger.transaction_date);
    setNote(ledger.note || "");
    setShowAddModal(true);
  };

  // Handle Page Change
  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(filteredLedger.length / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle Items Per Page Change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
  };

  // Pagination logic
  const displayedLedger = filteredLedger.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Handle Add/Edit Investment Form Submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const isEdit = editingLedger !== null;
    if (isEdit) {
      if (!canEditCapital || !canViewCapital) {
        toast.error("You do not have permission to edit capital investments.");
        return;
      }
    } else {
      if (!canAddCapital || !canViewCapital) {
        toast.error("You do not have permission to add capital investments.");
        return;
      }
    }
    try {
      const token = getErpToken();
      const isEdit = editingLedger !== null;

      const response = await fetch(`${node_apiUrl}/capital_investment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: isEdit ? "ledger_update" : "ledger_create",
          ...(isEdit && { id: editingLedger.id }),
          branch_id: getSessionBranchId(),
          user_admin_id: Number(investorId),
          direction: direction,
          amount: Number(amount),
          transaction_date: transactionDate,
          note: note || "",
        }),
      });

      const data = await response.json();

      if (data?.success) {
        toast.success(
          isEdit
            ? "Investment updated successfully"
            : "Investment added successfully",
        );
        setShowAddModal(false);

        // Reset form fields
        setInvestorId("");
        setDirection("IN");
        setAmount("");
        setTransactionDate("");
        setNote("");
        setEditingLedger(null);

        // Refresh ledger list
        fetchLedgerList({ force: true });
      } else {
        const msg =
          data?.message ||
          (isEdit ? "Failed to update investment" : "Failed to add investment");
        if (
          !isCapitalInvestmentViewDeniedMessage(msg) &&
          !isCapitalInvestmentEditDeniedMessage(msg)
        ) {
          toast.error(msg);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const msg =
        error.message ||
        (editingLedger
          ? "Failed to update investment"
          : "Failed to add investment");
      if (
        !isCapitalInvestmentViewDeniedMessage(msg) &&
        !isCapitalInvestmentEditDeniedMessage(msg)
      ) {
        toast.error(msg);
      }
    }
  };

  // Reset add form fields when modal closes
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setInvestorId("");
    setDirection("IN");
    setAmount("");
    setTransactionDate("");
    setNote("");
    setEditingLedger(null);
    resetInvestmentSearchState();
  };

  // Handle Stake Modal Close
  const handleCloseStakeModal = () => {
    setShowStakeModal(false);
    setStakes([{ user_admin_id: "", ownership_percent: "" }]);
    resetStakeSearchState();
  };

  // Handle Stake Save
  const handleStakeSave = async () => {
    if (!canEditCapital || !canViewCapital) {
      toast.error("You do not have permission to save stakes.");
      return;
    }
    // Validate stakes
    const validStakes = stakes.filter(
      (stake) => stake.user_admin_id && stake.ownership_percent,
    );

    if (validStakes.length === 0) {
      toast.error("Please add at least one stake");
      return;
    }

    if (!canAddCapital || !canViewCapital) {
      toast.error("You do not have permission to add capital investments.");
      return;
    }

    const totalPercentage = validStakes.reduce(
      (sum, stake) => sum + Number(stake.ownership_percent),
      0,
    );

    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error("Total ownership percentage must equal 100%");
      return;
    }

    try {
      const token = getErpToken();
      const response = await fetch(`${node_apiUrl}/capital_investment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: "stake_save",
          branch_id: getSessionBranchId(), // Get from ERP auth state
          stakes: validStakes.map((stake) => ({
            user_admin_id: Number(stake.user_admin_id), // Send proper user_admin_id
            ownership_percent: Number(stake.ownership_percent),
          })),
        }),
      });

      const data = await response.json();

      if (data?.success) {
        toast.success("Stakes saved successfully");
        handleCloseStakeModal();
      } else {
        const msg = data?.message || "Failed to save stakes";
        if (
          !isCapitalInvestmentViewDeniedMessage(msg) &&
          !isCapitalInvestmentEditDeniedMessage(msg)
        ) {
          toast.error(msg);
        }
      }
    } catch (error) {
      console.error("Error saving stakes:", error);
      const m = error.message || "Failed to save stakes";
      if (
        !isCapitalInvestmentViewDeniedMessage(m) &&
        !isCapitalInvestmentEditDeniedMessage(m)
      ) {
        toast.error(m);
      }
    }
  };

  // Handle adding new stake row
  const addStakeRow = () => {
    setStakes([...stakes, { user_admin_id: "", ownership_percent: "" }]);
  };

  // Handle removing stake row
  const removeStakeRow = (index) => {
    if (stakes.length > 1) {
      setStakes(stakes.filter((_, i) => i !== index));
    }
  };

  // Handle stake field change
  const handleStakeChange = (index, field, value) => {
    const newStakes = [...stakes];
    newStakes[index][field] = value;
    setStakes(newStakes);
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Capital Investment</h4>
            <div className="d-flex gap-2">
              {canAddCapital && (
                <>
                  <button
                    type="button"
                    className={`btn btn-primary ${buttonClass}`}
                    disabled={!isCurrentFinancialYear}
                    title={
                      !isCurrentFinancialYear
                        ? FY_ADD_DISABLED_TITLE
                        : undefined
                    }
                    onClick={() => {
                      if (!isCurrentFinancialYear) return;
                      resetStakeSearchState();
                      setShowStakeModal(true);
                    }}
                  >
                    Add Stake
                  </button>
                  <button
                    type="button"
                    className={`btn btn-success ${buttonClass}`}
                    disabled={!isCurrentFinancialYear}
                    title={
                      !isCurrentFinancialYear
                        ? FY_ADD_DISABLED_TITLE
                        : undefined
                    }
                    onClick={() => {
                      if (!isCurrentFinancialYear) return;
                      resetInvestmentSearchState();
                      setShowAddModal(true);
                    }}
                  >
                    Add Investment
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                disabled={!canViewCapital}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className={`form-select w-auto ms-3 ${buttonClass}`}
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                disabled={!canViewCapital}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Directions</option>
                <option value="IN">IN</option>
                <option value="OUT">OUT</option>
              </select>
            </div>

            {/* Ledger List Section */}
            <div>
              {/* <h5 className="mb-3">Ledger List</h5> */}
              <div className={`table-responsive mb-4 border ${cardClass}`}>
                <table
                  className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                >
                  <thead className="text-dark fs-4">
                    <tr>
                      <th>Investor Name</th>
                      <th>Direction</th>
                      <th>Amount</th>
                      <th>Transaction Date</th>
                      <th>Note</th>
                      {canEditCapital && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {canViewCapital && isLoading ? (
                      <tr>
                        <td colSpan={canEditCapital ? "6" : "5"} className="text-center py-4 text-muted">
                          Loading...
                        </td>
                      </tr>
                    ) : !canViewCapital ? (
                      <tr>
                        <td colSpan={canEditCapital ? "6" : "5"} className="text-center py-4 text-muted">
                          <CapitalInvestmentNoViewTableMessage />
                        </td>
                      </tr>
                    ) : displayedLedger.length > 0 ? (
                      displayedLedger.map((ledger) => (
                        <tr key={ledger.id}>
                          <td>{ledger.investor_name || "N/A"}</td>
                          <td>
                            <span
                              className={`badge ${ledger.direction === "IN"
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                                }`}
                            >
                              {ledger.direction}
                            </span>
                          </td>
                          <td>₹{Number(ledger.amount).toLocaleString()}</td>
                          <td>{formatDateToDDMMYYYY(ledger.transaction_date)}</td>
                          <td>{ledger.note && ledger.note.trim() ? ledger.note : "-"}</td>
                          {canEditCapital && (
                            <td>
                              <button
                                type="button"
                                className={`btn btn-primary btn-sm me-2 rounded-1 ${buttonClass}`}
                                onClick={() => handleEditClick(ledger)}
                                title="Edit"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                  role="img"
                                  className="iconify iconify--solar"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    fill="currentColor"
                                    d="m11.4 18.161l7.396-7.396a10.3 10.3 0 0 1-3.326-2.234a10.3 10.3 0 0 1-2.235-3.327L5.839 12.6c-.577.577-.866.866-1.114 1.184a6.6 6.6 0 0 0-.749 1.211c-.173.364-.302.752-.56 1.526l-1.362 4.083a1.06 1.06 0 0 0 1.342 1.342l4.083-1.362c.775-.258 1.162-.387 1.526-.56q.647-.308 1.211-.749c.318-.248.607-.537 1.184-1.114m9.448-9.448a3.932 3.932 0 0 0-5.561-5.561l-.887.887l.038.111a8.75 8.75 0 0 0 2.092 3.32a8.75 8.75 0 0 0 3.431 2.13z"
                                  />
                                </svg>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={canEditCapital ? "6" : "5"} className="text-center">
                          No ledger entries found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <Pagination
                totalItems={filteredLedger.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Investment Modal */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        centered
        contentClassName={cardClass}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingLedger ? "Edit Investment" : "Add Investment"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form noValidate="" onSubmit={handleAddSubmit}>
            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">Investor </label>
                <Select
                  value={
                    investorId
                      ? {
                        value: investorId,
                        label: investorLookup[investorId] || investorId,
                      }
                      : null
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setInvestorLookup((prev) => ({
                        ...prev,
                        [selectedOption.value]: selectedOption.label,
                      }));
                    }
                    setInvestorId(selectedOption ? selectedOption.value : "");
                  }}
                  options={investmentInvestorOptions}
                  placeholder="Select Investor"
                  isClearable
                  isSearchable
                  filterOption={() => true}
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      investmentSearchInputRef.current = inputValue;
                      void searchStakeholders(inputValue);
                    }
                  }}
                  isLoading={investmentOptionsLoading}
                  loadingMessage={() => "Searching investors..."}
                  noOptionsMessage={() =>
                    modalSearchNoOptionsMessage(
                      investmentOptionsLoading,
                      investmentSearchInputRef.current.length,
                    )
                  }
                  menuPortalTarget={document.body}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      minHeight: "38px",
                      fontSize: "14px",
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">
                  Direction 
                </label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      type="radio"
                      name="direction"
                      className="form-check-input"
                      value="IN"
                      checked={direction === "IN"}
                      onChange={(e) => setDirection(e.target.value)}
                    />
                    <label className="form-check-label">IN</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="direction"
                      className="form-check-input"
                      value="OUT"
                      checked={direction === "OUT"}
                      onChange={(e) => setDirection(e.target.value)}
                    />
                    <label className="form-check-label">OUT</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">
                  Amount 
                </label>
                <input
                  type="number"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">
                  Transaction Date 
                </label>
                <input
                  type="date"
                  className={`form-control ${buttonClass}`}
                  required
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">Note</label>
                <textarea
                  className={`form-control ${buttonClass}`}
                  rows={3}
                  placeholder="Enter Note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <button
                type="submit"
                className={`btn btn-primary ${buttonClass}`}
              >
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Add Stake Modal */}
      <Modal
        show={showStakeModal}
        onHide={handleCloseStakeModal}
        centered
        contentClassName={cardClass}
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Stake</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {stakes.map((stake, index) => (
              <div key={index} className="mb-3">
                <div className="d-flex gap-2 align-items-center">
                  <div className="flex-grow-1">
                    <label className="form-label">Investor </label>
                    <Select
                      value={
                        stake.user_admin_id
                          ? {
                            value: stake.user_admin_id,
                            label:
                              investorLookup[stake.user_admin_id] ||
                              stake.user_admin_id,
                          }
                          : null
                      }
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setInvestorLookup((prev) => ({
                            ...prev,
                            [selectedOption.value]: selectedOption.label,
                          }));
                        }
                        handleStakeChange(
                          index,
                          "user_admin_id",
                          selectedOption ? selectedOption.value : "",
                        );
                      }}
                      options={stakeInvestorOptions}
                      placeholder="Select Investor"
                      isClearable
                      isSearchable
                      filterOption={() => true}
                      onInputChange={(inputValue, { action }) => {
                        if (action === "input-change") {
                          stakeSearchInputRef.current = inputValue;
                          void searchEmployees(inputValue);
                        }
                      }}
                      isLoading={stakeOptionsLoading}
                      loadingMessage={() => "Searching investors..."}
                      noOptionsMessage={() =>
                        modalSearchNoOptionsMessage(
                          stakeOptionsLoading,
                          stakeSearchInputRef.current.length,
                        )
                      }
                      menuPortalTarget={document.body}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          fontSize: "14px",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                  <div style={{ width: "150px" }}>
                    <label className="form-label">Ownership % </label>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${buttonClass}`}
                      placeholder="%"
                      value={stake.ownership_percent}
                      onChange={(e) =>
                        handleStakeChange(
                          index,
                          "ownership_percent",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  {stakes.length > 1 && (
                    <div className="mt-4">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeStakeRow(index)}
                      >
                        <Icon icon="solar:trash-bin-trash-bold" width="16" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="row">
            <div className="col-md-12">
              <button
                type="button"
                className={`btn btn-outline-primary ${buttonClass}`}
                onClick={addStakeRow}
              >
                {/* <Icon
                  icon="solar:add-circle-bold"
                  width="16"
                  className="me-2"
                /> */}
                Add Another Stake
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-end">
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleStakeSave}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CapitalInvestmentTable;
